import { useMemo, useState, type FormEvent } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronsUp, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

interface LocationState {
  from?: { pathname?: string; search?: string };
}

type LoginMode = "password" | "reset";

function safeInternalRedirect(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return null;
  }

  return value;
}

export default function Login() {
  const { session, isLoading, signInWithPassword, requestPasswordReset } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = useMemo(() => {
    const redirectParam = safeInternalRedirect(searchParams.get("redirect"));
    if (redirectParam) return redirectParam;

    const stateFrom = (location.state as LocationState | null)?.from;
    const statePath = stateFrom?.pathname
      ? `${stateFrom.pathname}${stateFrom.search ?? ""}`
      : null;
    return safeInternalRedirect(statePath) ?? "/dashboard";
  }, [location.state, searchParams]);

  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<LoginMode>("password");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Already signed in? Bounce to where the user was headed.
  if (!isLoading && session) {
    return <Navigate to={from} replace />;
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);

    try {
      const { error } = await signInWithPassword(email.trim(), password);
      if (error) {
        setErrorMsg(error.message);
      }
      // On success, AuthContext's onAuthStateChange flips session and the
      // <Navigate> at the top of this component takes the user away.
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);

    try {
      const { error } = await requestPasswordReset(email.trim());

      if (error) {
        setErrorMsg(error.message);
        return;
      }

      setResetEmailSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  function showResetMode() {
    setMode("reset");
    setErrorMsg(null);
    setResetEmailSent(false);
  }

  function showPasswordMode() {
    setMode("password");
    setErrorMsg(null);
    setResetEmailSent(false);
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background px-6 py-12">
      {/* Ambient brand atmosphere — coral + gold blooms behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-coral/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-32 h-96 w-96 rounded-full bg-gold/15 blur-3xl"
      />

      <div className="relative w-full max-w-sm space-y-7">
        {/* Brand lockup */}
        <div className="flex animate-rise flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-coral shadow-glow">
            <ChevronsUp className="h-8 w-8 text-white" strokeWidth={2.75} />
          </span>
          <h1 className="mt-4 font-display text-4xl">
            <span className="text-gradient-coral">Relay</span>
          </h1>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            by Inspired Edge Group
          </p>
        </div>

        <Card
          className="animate-rise shadow-elevated"
          style={{ animationDelay: "80ms" }}
        >
          <CardHeader>
            {mode === "password" ? (
              <>
                <CardTitle className="text-base">Sign in</CardTitle>
                <CardDescription>Use your work email + password.</CardDescription>
              </>
            ) : resetEmailSent ? (
              <>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Check your email
                </CardTitle>
                <CardDescription>
                  If a Relay account exists for {email.trim() || "that address"}, we sent a
                  password setup link.
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-base">Set or reset password</CardTitle>
                <CardDescription>
                  Enter your work email and we'll send a link to choose a new password.
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {mode === "password" ? (
              <form className="space-y-4" onSubmit={handlePasswordSubmit}>
                <div>
                  <Label htmlFor="email" className="mb-1.5 block">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                      onClick={showResetMode}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                {errorMsg && (
                  <p className="text-sm text-destructive" role="alert">
                    {errorMsg}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="gradient"
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </form>
            ) : resetEmailSent ? (
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  Open the newest email from Relay and follow the link to set your password. If the
                  link is expired, return here and request a fresh one.
                </p>
                <Button type="button" variant="outline" className="w-full" onClick={showPasswordMode}>
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleResetSubmit}>
                <div>
                  <Label htmlFor="reset-email" className="mb-1.5 block">
                    Email
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                {errorMsg && (
                  <p className="text-sm text-destructive" role="alert">
                    {errorMsg}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="gradient"
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Send password setup link"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={submitting}
                  className="w-full"
                  onClick={showPasswordMode}
                >
                  Back to sign in
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <nav style={{ marginTop: 18, textAlign: "center", fontSize: 12 }}>
          <a href="/privacy" style={{ color: "#9b9b9b", textDecoration: "none" }}>Privacy</a>
          <span style={{ color: "#c9c9c9" }}> · </span>
          <a href="/terms" style={{ color: "#9b9b9b", textDecoration: "none" }}>Terms</a>
          <span style={{ color: "#c9c9c9" }}> · </span>
          <a href="/data-deletion" style={{ color: "#9b9b9b", textDecoration: "none" }}>Data deletion</a>
        </nav>
      </div>
    </div>
  );
}
